/**
 * Rounding modes supported by Money arithmetic.
 *
 * Default is `half-up` (a.k.a. "round half away from zero" for positive
 * amounts; we extend it symmetrically for negatives). This matches IRS
 * and most VAT authority guidance and matches user intuition
 * ("$0.125 rounds to $0.13").
 *
 * `half-even` (banker's rounding) is available for consumers that need
 * bias-free cumulative math (e.g. some EU VAT breakdowns, financial
 * aggregations).
 */
export type RoundingMode =
  | 'half-up'
  | 'half-even'
  | 'half-down'
  | 'up'
  | 'down'
  | 'ceiling'
  | 'floor';

export const DEFAULT_ROUNDING: RoundingMode = 'half-up';

/**
 * Divide `numerator` by `denominator` using the given rounding mode and
 * return the integer (bigint) result. Handles negative operands
 * symmetrically.
 *
 * `denominator` MUST be non-zero. Callers should assert this at their
 * boundary so this stays pure.
 */
export function divRound(
  numerator: bigint,
  denominator: bigint,
  mode: RoundingMode = DEFAULT_ROUNDING,
): bigint {
  if (denominator === 0n) {
    throw new Error('divRound: denominator must be non-zero');
  }

  // Normalise signs so the magnitude math below is unsigned; we
  // re-apply the sign at the end.
  const negative = numerator < 0n !== denominator < 0n;
  const absN = numerator < 0n ? -numerator : numerator;
  const absD = denominator < 0n ? -denominator : denominator;

  const quotient = absN / absD;
  const remainder = absN % absD;

  if (remainder === 0n) {
    return negative ? -quotient : quotient;
  }

  const doubled = remainder * 2n;
  const cmp = doubled < absD ? -1 : doubled > absD ? 1 : 0;

  let rounded: bigint;
  switch (mode) {
    case 'up':
      rounded = quotient + 1n;
      break;
    case 'down':
      rounded = quotient;
      break;
    case 'ceiling':
      rounded = negative ? quotient : quotient + 1n;
      break;
    case 'floor':
      rounded = negative ? quotient + 1n : quotient;
      break;
    case 'half-up':
      rounded = cmp >= 0 ? quotient + 1n : quotient;
      break;
    case 'half-down':
      rounded = cmp > 0 ? quotient + 1n : quotient;
      break;
    case 'half-even':
      if (cmp > 0) {
        rounded = quotient + 1n;
      } else if (cmp < 0) {
        rounded = quotient;
      } else {
        // Exactly halfway — round to even magnitude.
        rounded = quotient % 2n === 0n ? quotient : quotient + 1n;
      }
      break;
    default: {
      const _exhaustive: never = mode;
      throw new Error(`Unknown rounding mode: ${String(_exhaustive)}`);
    }
  }

  return negative ? -rounded : rounded;
}

/**
 * Raise 10 to a non-negative integer power as a bigint.
 * Throws on negative or non-integer exponents.
 */
export function pow10(exponent: number): bigint {
  if (!Number.isInteger(exponent) || exponent < 0) {
    throw new Error(`pow10: exponent must be a non-negative integer, got ${exponent}`);
  }
  let result = 1n;
  for (let i = 0; i < exponent; i++) {
    result *= 10n;
  }
  return result;
}
